import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Ticket, Users, Plus, Search, X } from 'lucide-react';
import { serviceTicketService, ServiceTicket } from '../utils/services/serviceTicketService';
import { Vendor } from '../types/vendor';
import { getCompanyId } from '../utils/tenantContext';
import { TicketFilters } from './service-tickets/TicketFilters';
import { TicketList } from './service-tickets/TicketList';
import { VendorList } from './service-tickets/VendorList';
import { CreateTicketForm } from './service-tickets/CreateTicketForm';
import { TicketDetailsSheet } from './service-tickets/TicketDetailsSheet';
import { VendorDialog } from './service-tickets/VendorDialog';
import VendorInviteModal from './VendorInviteModal';
import { vendorService } from '../utils/vendors/vendorService';
import { getUsersByCompany, AuthUser } from '../utils/auth/userService';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { cn } from './ui/utils';

interface ServiceTicketsProps {
    wells?: any[];
    currentUser?: { id: string; name: string; email: string; role?: string };
    onNavigate?: (screen: string) => void;
    onOpenXAssist?: () => void;
    isDarkMode?: boolean;
    onToggleTheme?: () => void;
    onClose?: () => void;
    initialWellId?: string | null;
}

type TabType = 'tickets' | 'vendors';
type TicketStatusFilter = 'all' | 'open' | 'progress' | 'completed';

export default function ServiceTickets({
    wells = [],
    currentUser,
    onNavigate = () => { },
    onOpenXAssist = () => { },
    isDarkMode = false,
    onToggleTheme = () => { },
    onClose,
    initialWellId
}: ServiceTicketsProps) {
    const [activeTab, setActiveTab] = useState<TabType>('tickets');
    const [ticketFilter, setTicketFilter] = useState<TicketStatusFilter>('all');
    const [searchQuery, setSearchQuery] = useState('');

    // Modals
    const [showNewTicket, setShowNewTicket] = useState(!!initialWellId);

    // Details Sheet State
    const [selectedTicket, setSelectedTicket] = useState<ServiceTicket | null>(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);

    // Vendor Management State
    const [showVendorDialog, setShowVendorDialog] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);

    // Data
    const [tickets, setTickets] = useState<ServiceTicket[]>([]);
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [users, setUsers] = useState<AuthUser[]>([]);
    const [loading, setLoading] = useState(true);

    const companyId = getCompanyId();
    const isVendorUser = currentUser?.role === 'vendor';

    // Subscriptions
    useEffect(() => {
        if (!companyId) return;

        const unsubTickets = serviceTicketService.subscribeToTickets((data) => {
            setTickets(data);
            setLoading(false);
        });

        const unsubVendors = serviceTicketService.subscribeToVendors((data) => {
            setVendors(data);
        });

        // Fetch users for assignment
        getUsersByCompany().then(setUsers).catch(console.error);

        return () => {
            unsubTickets();
            unsubVendors();
        };
    }, [companyId]);

    // Derived Status Configs
    const ticketStatusCounts = {
        all: tickets.length,
        open: tickets.filter(t => t.status === 'open').length,
        progress: tickets.filter(t => t.status === 'in_progress').length,
        completed: tickets.filter(t => t.status === 'resolved').length,
    };

    const mapFilterToStatus = (filter: TicketStatusFilter) => {
        if (filter === 'progress') return 'in_progress';
        if (filter === 'completed') return 'resolved';
        return filter;
    };

    // Filter Logic
    const filteredTickets = tickets.filter(t => {
        if (isVendorUser && t.vendorId !== currentUser?.id) return false;

        const matchesStatus = ticketFilter === 'all' || t.status === mapFilterToStatus(ticketFilter);
        const matchesSearch = t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            t.ticketNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
            t.wellName?.toLowerCase().includes(searchQuery.toLowerCase());

        return matchesStatus && matchesSearch;
    });

    const handleTicketClick = (ticket: ServiceTicket) => {
        setSelectedTicket(ticket);
        setIsDetailsOpen(true);
    };

    const handleAddVendor = () => {
        setEditingVendor(null);
        setShowVendorDialog(true);
    };

    const handleEditVendor = (vendor: Vendor) => {
        setEditingVendor(vendor);
        setShowVendorDialog(true);
    };

    const handleDeleteVendor = async (vendorId: string) => {
        if (confirm('Are you sure you want to delete this vendor?')) {
            try {
                await vendorService.deleteVendor(vendorId, currentUser?.id || 'unknown');
                toast.success('Vendor deleted');
            } catch (error) {
                toast.error('Failed to delete vendor');
            }
        }
    };

    const handleInviteVendor = () => {
        setShowInviteModal(true);
    };

    return (
        <div className={cn(
            "flex flex-col h-full min-h-screen",
            isDarkMode
                ? "bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950"
                : "bg-gradient-to-b from-gray-50 via-white to-gray-50"
        )}>
            {/* Main Content Container with Padding */}
            <div className="px-4 py-6 pb-32 max-w-7xl mx-auto w-full">

                {/* Header */}
                <header className="mb-6 flex justify-between items-start">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                            {activeTab === 'tickets' ? 'Service Tickets' : 'Vendor Management'}
                            {loading && <span className="text-sm font-normal text-slate-400 animate-pulse">(Syncing...)</span>}
                        </h1>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            {activeTab === 'tickets' ? 'Track maintenance and repair requests' : 'Manage external service providers'}
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Create Ticket Button */}
                        {activeTab === 'tickets' && !isVendorUser && (
                            <Button
                                onClick={() => setShowNewTicket(true)}
                                className="bg-blue-600 hover:bg-blue-700 text-white flex items-center shadow-sm active:scale-95 transition-all"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Create Ticket
                            </Button>
                        )}
                        {activeTab === 'vendors' && !isVendorUser && (
                            <Button
                                onClick={handleAddVendor}
                                className="hidden sm:flex bg-blue-600 hover:bg-blue-700 text-white"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Add Vendor
                            </Button>
                        )}

                        {onClose && (
                            <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full">
                                <X className="w-5 h-5 text-slate-500" />
                            </Button>
                        )}
                    </div>
                </header>

                {/* Tab Switcher (Non-Vendors Only) */}
                {!isVendorUser && (
                    <div className={cn(
                        "flex gap-2 mb-6 p-1.5 rounded-2xl",
                        isDarkMode ? "bg-slate-800/50" : "bg-gray-100"
                    )}>
                        <button
                            onClick={() => setActiveTab('tickets')}
                            className={cn(
                                "flex-1 flex items-center justify-center gap-2 py-3 rounded-xl transition-all font-medium",
                                activeTab === 'tickets'
                                    ? "bg-gradient-to-br from-rose-600 to-pink-700 text-white shadow-lg"
                                    : (isDarkMode ? "text-gray-400 hover:text-gray-300" : "text-gray-600 hover:text-gray-900")
                            )}
                        >
                            <Ticket className="w-4 h-4" />
                            <span className="text-sm">Tickets</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('vendors')}
                            className={cn(
                                "flex-1 flex items-center justify-center gap-2 py-3 rounded-xl transition-all font-medium",
                                activeTab === 'vendors'
                                    ? "bg-gradient-to-br from-purple-600 to-violet-700 text-white shadow-lg"
                                    : (isDarkMode ? "text-gray-400 hover:text-gray-300" : "text-gray-600 hover:text-gray-900")
                            )}
                        >
                            <Users className="w-4 h-4" />
                            <span className="text-sm">Vendors</span>
                        </button>
                    </div>
                )}

                {/* Content Area */}
                <AnimatePresence mode="wait">
                    {activeTab === 'tickets' ? (
                        <motion.div
                            key="tickets-view"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 10 }}
                            transition={{ duration: 0.2 }}
                            className="space-y-6"
                        >
                            {/* Filters and Search */}
                            <div className="space-y-4">
                                <div className="relative">
                                    <Search className={cn(
                                        "absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5",
                                        isDarkMode ? "text-gray-500" : "text-gray-400"
                                    )} />
                                    <input
                                        placeholder="Search tickets..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className={cn(
                                            "w-full pl-12 pr-4 py-3 rounded-xl text-sm border focus:outline-none focus:ring-2 focus:ring-rose-500",
                                            isDarkMode
                                                ? "bg-slate-800/50 border-slate-700 text-white placeholder-gray-500"
                                                : "bg-gray-100 border-gray-200 text-gray-900 placeholder-gray-500"
                                        )}
                                    />
                                </div>

                                <TicketFilters
                                    currentFilter={ticketFilter}
                                    counts={ticketStatusCounts}
                                    onFilterChange={setTicketFilter}
                                    isDarkMode={isDarkMode}
                                />
                            </div>

                            {/* Ticket List */}
                            <TicketList
                                tickets={filteredTickets}
                                onTicketClick={handleTicketClick}
                                isDarkMode={isDarkMode}
                            />
                        </motion.div>
                    ) : (
                        <motion.div
                            key="vendors-view"
                            initial={{ opacity: 0, x: 10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            transition={{ duration: 0.2 }}
                        >
                            <VendorList
                                vendors={vendors}
                                onEdit={handleEditVendor}
                                onDelete={handleDeleteVendor}
                                onInvite={handleInviteVendor}
                                onAdd={handleAddVendor}
                                isDarkMode={isDarkMode}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* New Ticket Modal */}
                <CreateTicketForm
                    isOpen={showNewTicket}
                    onClose={() => setShowNewTicket(false)}
                    onSuccess={() => setShowNewTicket(false)}
                    wells={wells}
                    vendors={vendors}
                    initialWellId={initialWellId}
                    currentUser={currentUser}
                    isDarkMode={isDarkMode}
                />

                {/* Ticket Details Sheet */}
                <TicketDetailsSheet
                    ticket={selectedTicket}
                    open={isDetailsOpen}
                    onClose={() => {
                        setIsDetailsOpen(false);
                        setSelectedTicket(null);
                    }}
                    onUpdate={() => { }}
                    users={users}
                    isDarkMode={isDarkMode}
                />

                {/* Vendor Modals */}
                <VendorDialog
                    isOpen={showVendorDialog}
                    onClose={() => setShowVendorDialog(false)}
                    onSuccess={() => {
                        setShowVendorDialog(false);
                        // Refresh logic handled by subscription
                    }}
                    vendor={editingVendor}
                    currentUserId={currentUser?.id || ''}
                    currentUserName={currentUser?.name || 'Admin'}
                    isDarkMode={isDarkMode}
                />

                {currentUser && (
                    <VendorInviteModal
                        isOpen={showInviteModal}
                        onClose={() => setShowInviteModal(false)}
                        onSuccess={() => setShowInviteModal(false)}
                        user={{
                            id: currentUser.id,
                            name: currentUser.name || 'Admin',
                            email: currentUser.email || '',
                        }}
                        company={{
                            id: companyId || 'unknown',
                            name: 'OilWellX', // TODO: Fetch actual company name
                        }}
                    />
                )}

            </div>
        </div>
    );
}
